import { Fragment, memo, useMemo } from "react";
import { keyframes } from "@emotion/react";
import { Box, Link, Typography } from "@mui/material";
import { parseMarkdown } from "src/_modules/AiSupportChat/core/markdown";

const blink = keyframes`
  50% { opacity: 0; }
`;

/** Blinking caret at the end of an answer that is still streaming. */
const Caret = () => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      display: "inline-block",
      width: "0.45em",
      height: "1em",
      marginInlineStart: "2px",
      verticalAlign: "text-bottom",
      bgcolor: "text.primary",
      opacity: 0.6,
      animation: `${blink} 1s steps(1) infinite`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  />
);

const CODE_SX = {
  fontFamily: "monospace",
  fontSize: "0.9em",
  bgcolor: "action.selected",
  borderRadius: 0.5,
  px: 0.5,
  overflowWrap: "anywhere",
};

const BLOCK_SX = { m: 0, overflowWrap: "anywhere" };

const renderInlineNode = (node, key) => {
  switch (node.type) {
    case "text":
      return <Fragment key={key}>{node.value}</Fragment>;
    case "code":
      return (
        <Box key={key} component="code" sx={CODE_SX}>
          {node.value}
        </Box>
      );
    case "br":
      return <br key={key} />;
    case "strong":
      return <strong key={key}>{renderInline(node.children)}</strong>;
    case "em":
      return <em key={key}>{renderInline(node.children)}</em>;
    case "link":
      // `href` already passed the link policy (core/markdown)
      return (
        <Link
          key={key}
          href={node.href}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
        >
          {renderInline(node.children)}
        </Link>
      );
    default:
      return null;
  }
};

const renderInline = (nodes) => nodes.map(renderInlineNode);

/**
 * A list keeps one direction for its markers and padding (`dir="auto"` on
 * the list: the first item decides); each item still orders its own text
 * by content (`unicode-bidi: plaintext`). A `dir` per item would hang an
 * English item's marker outside an Arabic list.
 */
const renderList = (list, key, caret) => {
  const last = list.items.length - 1;
  return (
    <Box
      key={key}
      component={list.ordered ? "ol" : "ul"}
      start={list.ordered && list.start !== null ? list.start : undefined}
      dir="auto"
      sx={{
        ...BLOCK_SX,
        paddingInlineStart: 2.5,
        typography: "body2",
        "& li": { unicodeBidi: "plaintext" },
        "& li + li": { mt: 0.25 },
      }}
    >
      {list.items.map((item, index) =>
        renderListItem(item, index, index === last ? caret : null)
      )}
    </Box>
  );
};

const renderListItem = (item, key, caret) => {
  const lastChild = item.children[item.children.length - 1];
  const caretInNestedList = caret && lastChild?.type === "list";
  return (
    <li key={key}>
      {item.children.map((child, index) =>
        child.type === "list"
          ? renderList(
              child,
              index,
              caretInNestedList && child === lastChild ? caret : null
            )
          : renderInlineNode(child, index)
      )}
      {caret && !caretInNestedList && caret}
    </li>
  );
};

const renderBlock = (block, key, caret) => {
  switch (block.type) {
    case "paragraph":
    case "heading":
      // A heading is a bold paragraph: no document headings inside a bubble
      return (
        <Typography
          key={key}
          variant="body2"
          component="p"
          dir="auto"
          sx={{
            ...BLOCK_SX,
            fontWeight: block.type === "heading" ? 700 : undefined,
          }}
        >
          {renderInline(block.children)}
          {caret}
        </Typography>
      );
    case "list":
      return renderList(block, key, caret);
    default:
      return null;
  }
};

/**
 * An assistant answer: the safe markdown subset of core/markdown rendered
 * as React elements (plan §7). Never injects HTML. Every block is
 * `dir="auto"`, so an English answer in the Arabic UI reads LTR.
 *
 * @param {object} props
 * @param {string} props.text the answer so far
 * @param {boolean} [props.streaming] shows a blinking caret after the text
 */
function MarkdownMessage({ text, streaming = false }) {
  // Parsed once per text change (every frame while streaming)
  const root = useMemo(() => parseMarkdown(text), [text]);
  const caret = streaming ? <Caret /> : null;
  const blocks = root.children;

  if (!blocks.length) {
    return caret ? (
      <Typography variant="body2" component="p" sx={BLOCK_SX}>
        {caret}
      </Typography>
    ) : null;
  }

  const last = blocks.length - 1;
  return (
    <Box sx={{ "& > * + *": { mt: 1 } }}>
      {blocks.map((block, index) =>
        renderBlock(block, index, index === last ? caret : null)
      )}
    </Box>
  );
}

export default memo(MarkdownMessage);
